import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, EMPTY, map, of, switchMap, tap } from 'rxjs';
import { GamesActions } from '../../../games/store/actions/actions-type.action';
import { ModalWindowsService } from '../../../modals/services/modal-windows.service';
import { BadResponseWithError } from '../../models/bad-response-with-error.interface';
import {
    SearchForPlayersResponse,
    SearchForTeamsResponse,
    SupervisorSearchItem,
} from '../../models/common.interface';
import { CommonService } from '../../services/common.service';
import { CommonSettingsActions } from '../actions/actions-type.action';

@Injectable()
export class CommonSettingsEffect {
    private actions$ = inject(Actions);
    private modalService = inject(ModalWindowsService);
    private commonService = inject(CommonService);
    private router = inject(Router);

    closeCreateTournamentSecondStepModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CommonSettingsActions.closeCreateTournamentSecondStepModalAction
                ),
                tap(() => {
                    this.modalService.closeCreateTournamentSecondStepModal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    closeCreateTournamentSelectGameModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CommonSettingsActions.closeCreateTournamentSelectGameModalAction
                ),
                tap(() => {
                    this.modalService.closeCreateTournamentSelectGameModal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    closeSetupTournamentModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CommonSettingsActions.closeSetupTournamentModalAction),
                tap(({ payload }) => {
                    this.modalService.closeSetupTournamentModal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    navigateToTournamentSettings$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CommonSettingsActions.navigateToTournamentSettings),
                tap(({ payload }) => {
                    this.router.navigateByUrl(
                        `/tournament/${payload.tournamentId}/settings/controlPanel`
                    );
                })
            ),
        { dispatch: false }
    );

    openCreateTournamentSecondStepModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GamesActions.getGameByIdSuccess),
                tap((action) => {
                    if (action.payload.openCrateTournamentModal) {
                        const game = action.payload.data;
                        this.modalService.openCreateTournamentSecondStepModal({
                            game,
                        });
                    } else if (action.payload.isV2TournamentCreate) {
                        const game = action.payload.data;
                        this.modalService.openCreateTournamentSecondStepV2Modal({
                            game,
                        });
                    }

                }),
                catchError((err) => EMPTY)
            ),
        { dispatch: false }
    );

    openCreateTournamentSecondStepModalV1$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(GamesActions.getGameByIdV1Success),
                tap((action) => {
                    if (action.payload.openCrateTournamentModal) {
                        const game = action.payload.data;
                        this.modalService.openCreateTournamentSecondStepModal({ game });
                    }
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    openCreateTournamentSelectGameModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CommonSettingsActions.openCreateTournamentSelectGameModalAction
                ),
                tap(() => {
                    this.modalService.openCreateTournamentSelectGameModal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    openSetupTournamentModal$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CommonSettingsActions.openSetupTournamentModalAction),
                tap(() => {
                    this.modalService.openSetupTournamentModal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    searchPlayers$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommonSettingsActions.searchForPlayers),
            switchMap(({ payload }) =>
                this.commonService.searchForPlayers(payload).pipe(
                    map((response: SearchForPlayersResponse) =>
                        CommonSettingsActions.searchForPlayersSuccess({
                            payload: {
                                response,
                                patchData: payload.patchData ?? false,
                            },
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            CommonSettingsActions.searchForPlayersFailure({
                                payload: response,
                            })
                        )
                    )
                )
            )
        )
    );

    searchTeams$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommonSettingsActions.searchForTeams),
            switchMap(({ payload }) =>
                this.commonService.searchForTeams(payload).pipe(
                    map((response: SearchForTeamsResponse) =>
                        CommonSettingsActions.searchForTeamsSuccess({
                            payload: {
                                response,
                                patchData: payload.patchData ?? false,
                            },
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            CommonSettingsActions.searchForTeamsFailure({
                                payload: response,
                            })
                        )
                    )
                )
            )
        )
    );
    setRefCode$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommonSettingsActions.setRefCode),
            switchMap(({ payload }) =>
                {
                    return this.commonService.setRefCode(payload.refCode, payload.firstAccessTimestamp).pipe(
                        map((response: any) =>
                           CommonSettingsActions.setRefCodeSuccess()
                        ),
                        catchError((response: BadResponseWithError) =>
                            EMPTY
                        )
                    )
                }
            )
        ),
    );

    searchModerators$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommonSettingsActions.searchForModerators),
            switchMap(({ payload }) =>
                this.commonService.searchForSupervizer(payload).pipe(
                    map((response: SupervisorSearchItem[]) =>
                        CommonSettingsActions.searchForModeratorsSuccess({
                            payload: {
                                response,
                                patchData: payload.patchData ?? false,
                            },
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            CommonSettingsActions.searchForPlayersFailure({
                                payload: response,
                            })
                        )
                    )
                )
            )
        )

    );

    searchOperators$ = createEffect(() =>
        this.actions$.pipe(
            ofType(CommonSettingsActions.searchForOperators),
            switchMap(({ payload }) =>
                this.commonService.searchForSupervizer(payload).pipe(
                    map((response: SupervisorSearchItem[]) =>
                        CommonSettingsActions.searchForOperatorsSuccess({
                            payload: {
                                response,
                                patchData: payload.patchData ?? false,
                            },
                        })
                    ),
                    catchError((response: BadResponseWithError) =>
                        of(
                            CommonSettingsActions.searchForPlayersFailure({
                                payload: response,
                            })
                        )
                    )
                )
            )
        )
    );

    closeCreateTournamentSecondStepModalV2$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CommonSettingsActions.closeCreateTournamentSecondStepModalActionV2
                ),
                tap(() => {
                    this.modalService.closeCreateTournamentSecondStepV2Modal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    closeCreateTournamentSelectGameModalV2$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CommonSettingsActions.closeCreateTournamentSelectGameModalActionV2
                ),
                tap(() => {
                    this.modalService.closeCreateTournamentSelectGameModal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    closeSetupTournamentModalV2$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CommonSettingsActions.closeSetupTournamentModalActionV2),
                tap(({ payload }) => {
                    this.modalService.closeSetupTournamentModalV2();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    navigateToTournamentSettingsV2$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CommonSettingsActions.navigateToTournamentSettingsV2),
                tap(({ payload }) => {
                    this.router.navigateByUrl(
                        `/tournamentv2/${payload.tournamentId}/settings/controlPanel?v=2`
                    );
                })
            ),
        { dispatch: false }
    );

    openCreateTournamentSelectGameModalV2$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(
                    CommonSettingsActions.openCreateTournamentSelectGameModalActionV2
                ),
                tap(() => {
                    this.modalService.openCreateTournamentSelectGameModal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    openSetupTournamentModalV2$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CommonSettingsActions.openSetupTournamentModalActionV2),
                tap(() => {
                    this.modalService.openSetupTournamentModalV2();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );

    openCreateTournamentSecondStepModalV2$ = createEffect(
        () =>
            this.actions$.pipe(
                ofType(CommonSettingsActions.openCreateTournamentSecondStepModalActionV2),
                tap(() => {
                    this.modalService.openCreateTournamentSecondStepV2Modal();
                }),
                catchError(() => EMPTY)
            ),
        { dispatch: false }
    );
}
